import { useCallback } from "react";
import { getSceneState } from "../../../stores/sceneStore";
import { logger } from "../../../utils/logger";
import * as RuntimeRegistry from "../../../runtime/objectRegistry";
import {
  getSavedCameraWorldPose,
  getBodyWorldPoseFromRegistry,
} from "../../../utils/savedCameraWorldPose";
import { prunePrimarySelectionIfStaleAfterSceneGraphMutation } from "../helpers";
import { findModelGroupInObjectRegistry, localPoseFromWorldRelativeToBody } from "../helpers";

export function useCameraInertialContextMenuActions({
  contextMenuCameraIds,
  contextMenuInertialKeys,
  sceneActions,
  clearBatchSelection,
  runGroupedBodyAction,
}) {
  const handleDeleteCameras = useCallback(
    (cameraIds) => {
      if (!Array.isArray(cameraIds) || cameraIds.length === 0) return;
      runGroupedBodyAction(() => {
        cameraIds.forEach((id) => {
          sceneActions.removeCamera(id);
        });
      });
      prunePrimarySelectionIfStaleAfterSceneGraphMutation();
      clearBatchSelection();
    },
    [runGroupedBodyAction, sceneActions, clearBatchSelection]
  );

  const handleMoveCamerasToBody = useCallback(
    (targetBodyUuid, cameraIds = contextMenuCameraIds) => {
      if (!targetBodyUuid || !Array.isArray(cameraIds) || cameraIds.length === 0) return;
      const snapshot = getSceneState();
      if (!snapshot.bodies?.[targetBodyUuid]) return;

      const sceneObjects = RuntimeRegistry.getAll() || {};
      const modelGroup = findModelGroupInObjectRegistry();
      const bodiesMap = snapshot.bodies || {};
      const sitesMap = snapshot.sites || {};

      runGroupedBodyAction(() => {
        cameraIds.forEach((cameraUuid) => {
          const cam = snapshot.cameras?.[cameraUuid];
          if (!cam) return;
          if (
            cam.kind === "mounted" &&
            cam.mountType === "body" &&
            cam.mountBodyUuid === targetBodyUuid
          ) {
            return;
          }
          const world = getSavedCameraWorldPose(cam, sceneObjects, bodiesMap, sitesMap, modelGroup);
          const bodyPose = getBodyWorldPoseFromRegistry(sceneObjects, targetBodyUuid);
          if (!bodyPose) {
            logger.warn(
              `[SceneGraphDiagram] Reparent camera: no scene pose for body "${targetBodyUuid}". Is the 3D view loaded?`
            );
            return;
          }
          const local = localPoseFromWorldRelativeToBody(
            world.position,
            world.quaternion,
            bodyPose.position,
            bodyPose.quaternion
          );
          sceneActions.updateCamera(cameraUuid, {
            kind: "mounted",
            mountType: "body",
            mountBodyUuid: targetBodyUuid,
            mountSiteUuid: undefined,
            position: local.position,
            orientation: local.orientation,
          });
        });
      });
      clearBatchSelection();
    },
    [contextMenuCameraIds, runGroupedBodyAction, sceneActions, clearBatchSelection]
  );

  const handleDeleteInertials = useCallback(
    (inertialKeys) => {
      if (!Array.isArray(inertialKeys) || inertialKeys.length === 0) return;
      const bodyUuids = new Set();
      inertialKeys.forEach((key) => {
        const str = String(key || "");
        const sep = str.indexOf(":");
        const bodyUuid = sep > 0 ? str.slice(0, sep) : null;
        if (bodyUuid) bodyUuids.add(bodyUuid);
      });
      if (bodyUuids.size === 0) return;
      runGroupedBodyAction(() => {
        bodyUuids.forEach((bodyUuid) => {
          sceneActions.removeInertia(bodyUuid);
        });
      });
      prunePrimarySelectionIfStaleAfterSceneGraphMutation();
      clearBatchSelection();
    },
    [runGroupedBodyAction, sceneActions, clearBatchSelection]
  );

  const handleMoveInertialsToBody = useCallback(
    (targetBodyUuid, inertialKeys = contextMenuInertialKeys) => {
      if (!targetBodyUuid || !Array.isArray(inertialKeys) || inertialKeys.length !== 1) {
        return;
      }

      const sourceBodyUuids = new Set();
      inertialKeys.forEach((key) => {
        const str = String(key || "");
        const sep = str.indexOf(":");
        const bodyUuid = sep > 0 ? str.slice(0, sep) : null;
        if (bodyUuid && bodyUuid !== targetBodyUuid) sourceBodyUuids.add(bodyUuid);
      });

      if (sourceBodyUuids.size === 0) {
        clearBatchSelection();
        return;
      }

      runGroupedBodyAction(() => {
        sourceBodyUuids.forEach((sourceBodyUuid) => {
          sceneActions.moveInertiaToBody(sourceBodyUuid, targetBodyUuid);
        });
      });
      clearBatchSelection();
    },
    [contextMenuInertialKeys, runGroupedBodyAction, sceneActions, clearBatchSelection]
  );

  return {
    handleDeleteCameras,
    handleMoveCamerasToBody,
    handleDeleteInertials,
    handleMoveInertialsToBody,
  };
}
